import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const BRAND_COMBOBOX_PAGE_SIZE = 50;

interface BrandComboboxProps {
  value: string;
  onChange: (uuid: string, label: string, logoUrl?: string) => void;
  excludeUuid?: string;
  disabled?: boolean;
}

export const BrandCombobox = ({ value, onChange, excludeUuid, disabled }: BrandComboboxProps) => {
  const { t } = useTranslation("common");
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: BRAND_COMBOBOX_PAGE_SIZE,
  });

  const { data, isPending } = useBrandSearchQuery({
    options: {
      parameters: { pageSize, search: debouncedSearchTerm || undefined },
      body: {},
    },
  });

  const items = useMemo(() => data?.data ?? [], [data]);
  const total = data?.meta?.total ?? 0;

  const options = useMemo<ComboboxOption[]>(
    () =>
      items
        .filter((brand) => brand.uuid !== excludeUuid)
        .map((brand) => ({ value: brand.uuid ?? "", label: brand.name ?? "" })),
    [items, excludeUuid],
  );

  const { isLoading, hasMore, remainingCount, onLoadMore, isLoadingMore, onSearchChange } = createPaginationState(
    total,
    items.length,
    isPending,
  );

  const placeholder = t("SearchBrands");

  return (
    <Combobox
      options={options}
      value={value}
      onChange={(next) => {
        const selectedBrand = items.find((brand) => brand.uuid === next);
        onChange(next as string, selectedBrand?.name ?? "", selectedBrand?.logoUrl);
      }}
      onSearchChange={onSearchChange}
      shouldFilter={false}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      hasMore={hasMore}
      remainingCount={remainingCount}
      onLoadMore={onLoadMore}
      placeholder={placeholder}
      searchPlaceholder={placeholder}
      disabled={disabled}
    />
  );
};
